// hooks/participantScreen.ts
// Pure screen selection for the participant onboarding flow — NO imports from
// React, Firebase or localStorage. Companion to hooks/bootState.ts: that one
// answers "is boot still in progress?" for BootShield, this one answers "which
// onboarding screen should ParticipantNewPage render?".
//
// The rule this file exists to enforce: an indeterminate state NEVER renders a
// terminal screen. `claimsRole === null` means "not known yet" until claims are
// known AND data has settled AND no join is pending/in-flight/failed — only then
// does it mean "genuinely not provisioned".
import type { UserRole } from '../types';

export type ParticipantScreen =
  | 'loading'
  | 'login'
  | 'join'
  | 'profile'
  | 'welcome'
  | 'waiting'
  | 'unprovisioned';

export interface ParticipantScreenInputs {
  /** first onAuthStateChanged callback has fired */
  authResolved: boolean;
  hasUser: boolean;
  /** a token read (cached or fresh) completed for this user */
  claimsKnown: boolean;
  claimsRole: UserRole | null;
  /** first users/{uid} snapshot arrived, or its timeout fired */
  profileSettled: boolean;
  /** first onDataChange emission arrived, or its timeout fired */
  dataReady: boolean;
  /** a join is pending for a project the user is NOT already in
      (caller must not set this for an already-joined project) */
  pendingJoin: boolean;
  /** the joinProject call is running right now */
  joinInFlight: boolean;
  /** the last joinProject attempt threw — retryable, never a rejection */
  joinFailed: boolean;
  /** projectId is set AND the project document resolved in loaded data */
  hasResolvedProject: boolean;
  /** we have waited long enough for the project to arrive that its absence is
      terminal, not just the transient window before Firestore's cold tier
      lands. Without this, a slow project load reads as "you have no project". */
  projectSettled: boolean;
  profileComplete: boolean;
}

/**
 * "We know as much about the data as we ever will."
 *
 * Not simply `!isLoadingData`: that is true before appData has arrived, which
 * made the post-join window resolve to 'waiting'. But not simply
 * `!isLoadingData && hasAppData` either — a user with NO role claim never gets
 * appData at all. contexts/AppDataContext.tsx's `if (!claimsRole)` branch
 * settles isLoadingData and returns *before* setupListener, and every
 * setAppData call for a signed-in user lives inside that listener. Requiring
 * appData there waits forever and pins an unprovisioned user on the loader.
 */
export const isDataReady = (
  isLoadingData: boolean,
  hasAppData: boolean,
  claimsRole: UserRole | null,
): boolean => !isLoadingData && (hasAppData || claimsRole === null);

export function deriveParticipantScreen(i: ParticipantScreenInputs): ParticipantScreen {
  if (!i.authResolved) return 'loading';
  if (!i.hasUser) return 'login';
  // Claims/profile still settling: indeterminate, never a guard screen.
  if (!i.claimsKnown || !i.profileSettled) return 'loading';
  if (i.joinInFlight) return 'loading';
  // Already provisioned into a resolved project — nothing left to join, so a
  // stale pendingJoin flag must not drag the user back to the consent screen.
  if (i.claimsRole !== null && i.hasResolvedProject) {
    return i.profileComplete ? 'welcome' : 'profile';
  }
  // A failed join is retryable via the consent screen — NOT a rejection.
  if (i.joinFailed) return 'join';
  if (i.pendingJoin) return 'join';
  if (i.claimsRole === null) return i.dataReady ? 'unprovisioned' : 'loading';
  // "Provisioned but no project" is only true once the project has had its
  // chance to arrive — otherwise the normal post-join window (claims in,
  // project still loading) flashes a "no project assigned" screen.
  return i.dataReady && i.projectSettled ? 'waiting' : 'loading';
}
